import { POWERUP } from '../constants';
import type { Camera } from '../engine/Camera';
import type { Powerup } from '../entities/Powerup';

/**
 * Powerup pickups — premium "concept-art" pass.
 *
 * Each pickup is drawn in three layers so the silhouette reads from across
 * the arena and the iconography stays crisp at any zoom:
 *
 *   surge  → cyan lightning bolt with parallel motion-trail strokes behind
 *            it, a bright bolt-shaped fill, and a hot near-white core.
 *   shield → magenta hexagonal sphere — five tessellated cells visible at
 *            once + an animated bright outline, sitting inside an outer halo.
 *   double → gold disc + concentric HUD ring with tick marks rotating in
 *            opposite directions; bold "2×" glyph centered.
 *
 * The visual reference is the cellsnake_powerups.png concept art shown
 * on the landing. The shapes here are designed to feel like simplified
 * but recognizable versions of that art at gameplay scale.
 *
 * Performance: still purely procedural, frustum-culled, zero per-frame
 * allocations beyond the createRadialGradient/Pattern handles the canvas
 * API itself returns.
 */
const COLORS: Record<Powerup['kind'], { hue: number; sat: number; light: number; ringHue: number }> = {
  surge:  { hue: 195, sat: 100, light: 65, ringHue: 195 },
  shield: { hue: 320, sat: 100, light: 65, ringHue: 320 },
  double: { hue: 48,  sat: 100, light: 60, ringHue: 48  },
};

export class PowerupRenderer {
  draw(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    powerups: Powerup[],
    time: number,
  ): void {
    if (powerups.length === 0) return;
    const z = camera.zoom;
    const ox = camera.viewportW * 0.5 + camera.shakeX;
    const oy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;
    const bounds = camera.getVisibleBounds();
    const pad = POWERUP.RADIUS * 3;

    for (let i = 0; i < powerups.length; i++) {
      const p = powerups[i]!;
      if (p.x + pad < bounds.minX || p.x - pad > bounds.maxX) continue;
      if (p.y + pad < bounds.minY || p.y - pad > bounds.maxY) continue;
      const bob = Math.sin(time * 1.6 + p.bobPhase) * 4;
      const sx = (p.x - cax) * z + ox;
      const sy = (p.y - cay - bob * 0.25) * z + oy;
      const r = POWERUP.RADIUS * z;
      const c = COLORS[p.kind];

      // ── Outer pulse halo (shared) ──
      const pulse = 0.55 + 0.45 * Math.sin(time * 3.2 + p.bobPhase);
      const haloR = r * (1.7 + 0.35 * pulse);
      const haloGrad = ctx.createRadialGradient(sx, sy, r * 0.3, sx, sy, haloR);
      haloGrad.addColorStop(0, `hsla(${c.ringHue} ${c.sat}% ${c.light}% / ${0.5 * pulse + 0.25})`);
      haloGrad.addColorStop(1, `hsla(${c.ringHue} ${c.sat}% 50% / 0)`);
      ctx.fillStyle = haloGrad;
      ctx.beginPath();
      ctx.arc(sx, sy, haloR, 0, Math.PI * 2);
      ctx.fill();

      // ── Icon ──
      ctx.save();
      switch (p.kind) {
        case 'surge':  this.drawLightning(ctx, sx, sy, r, c, time + p.bobPhase); break;
        case 'shield': this.drawHexSphere(ctx, sx, sy, r, c, time + p.bobPhase); break;
        case 'double': this.drawDouble(ctx, sx, sy, r, c, time + p.bobPhase); break;
      }
      ctx.restore();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Surge — stylized lightning bolt with motion-trail
  // ─────────────────────────────────────────────────────────────────────────

  private drawLightning(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, r: number,
    c: { hue: number; sat: number; light: number },
    time: number,
  ): void {
    // Bolt control points — proper Z-shape with sharper angles. All offsets
    // are factors of r so the icon scales smoothly with zoom.
    const bolt: [number, number][] = [
      [cx - r * 0.10, cy - r * 0.85],
      [cx + r * 0.32, cy - r * 0.18],
      [cx + r * 0.05, cy - r * 0.05],
      [cx + r * 0.20, cy + r * 0.85],
      [cx - r * 0.30, cy + r * 0.10],
      [cx + r * 0.02, cy - r * 0.05],
    ];

    // ── Motion-trail: three parallel ghost bolts fading to the left,
    //    suggesting the bolt is whipping through the air. ──
    const trailCount = 3;
    for (let i = trailCount; i >= 1; i--) {
      const off = i * r * 0.18;
      const alpha = 0.12 + 0.06 * (trailCount - i);
      ctx.beginPath();
      ctx.moveTo(bolt[0]![0] - off, bolt[0]![1]);
      for (let j = 1; j < bolt.length; j++) {
        ctx.lineTo(bolt[j]![0] - off, bolt[j]![1]);
      }
      ctx.closePath();
      ctx.fillStyle = `hsla(${c.hue} 100% 70% / ${alpha})`;
      ctx.fill();
    }

    // ── Outer wide glow stroke ──
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = `hsl(${c.hue} 100% 70%)`;
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.moveTo(bolt[0]![0], bolt[0]![1]);
    for (let i = 1; i < bolt.length; i++) ctx.lineTo(bolt[i]![0], bolt[i]![1]);
    ctx.closePath();
    ctx.strokeStyle = `hsla(${c.hue} 100% 70% / 0.75)`;
    ctx.lineWidth = Math.max(2, r * 0.12);
    ctx.stroke();

    // ── Main bolt fill (bright body) ──
    ctx.shadowBlur = 8;
    const grad = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
    grad.addColorStop(0,   `hsl(${c.hue} 100% 88%)`);
    grad.addColorStop(0.5, `hsl(${c.hue} 100% 70%)`);
    grad.addColorStop(1,   `hsl(${c.hue} 100% 55%)`);
    ctx.fillStyle = grad;
    ctx.fill();

    // ── Hot white core stripe down the center of the bolt ──
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.moveTo(bolt[0]![0], bolt[0]![1]);
    ctx.lineTo(bolt[1]![0], bolt[1]![1]);
    ctx.lineTo(bolt[2]![0], bolt[2]![1]);
    ctx.lineTo(bolt[3]![0], bolt[3]![1]);
    ctx.strokeStyle = `hsla(0 0% 100% / ${0.65 + 0.25 * Math.sin(time * 8)})`;
    ctx.lineWidth = Math.max(1, r * 0.05);
    ctx.stroke();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Aegis — hexagonal sphere with five visible cells + crackle outline
  // ─────────────────────────────────────────────────────────────────────────

  private drawHexSphere(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, r: number,
    c: { hue: number; sat: number; light: number },
    time: number,
  ): void {
    const outer = r * 0.82;
    const rot = time * 0.4;

    // ── Sphere base shading — dark inner, bright rim ──
    const sphereGrad = ctx.createRadialGradient(cx - outer * 0.25, cy - outer * 0.3, 0, cx, cy, outer);
    sphereGrad.addColorStop(0,   `hsla(${c.hue} 100% 35% / 0.85)`);
    sphereGrad.addColorStop(0.7, `hsla(${c.hue} 100% 18% / 0.92)`);
    sphereGrad.addColorStop(1,   `hsla(${c.hue} 100% 8% / 0.95)`);
    ctx.fillStyle = sphereGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, outer, 0, Math.PI * 2);
    ctx.fill();

    // ── Tessellated hex grid clipped to the sphere ──
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, outer, 0, Math.PI * 2);
    ctx.clip();

    const hexR = outer * 0.32;
    const colW = hexR * Math.sqrt(3);
    const rowH = hexR * 1.5;
    // 5 visible cells: one center, four neighbors in pointy-top layout.
    const cells: [number, number][] = [
      [cx,             cy],
      [cx + colW,      cy],
      [cx - colW,      cy],
      [cx + colW / 2,  cy - rowH],
      [cx - colW / 2,  cy - rowH],
      [cx + colW / 2,  cy + rowH],
      [cx - colW / 2,  cy + rowH],
    ];
    ctx.lineWidth = Math.max(1.2, r * 0.04);
    for (const [hx, hy] of cells) {
      // Cell fill — translucent so the sphere shading shows through.
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = Math.PI / 2 + i * (Math.PI / 3); // pointy-top
        const px = hx + Math.cos(a) * hexR;
        const py = hy + Math.sin(a) * hexR;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = `hsla(${c.hue} 100% 55% / 0.25)`;
      ctx.fill();
      ctx.strokeStyle = `hsla(${c.hue} 100% 75% / 0.85)`;
      ctx.stroke();
    }
    ctx.restore();

    // ── Crackle outline — animated bright ring around the sphere ──
    const crackleAlpha = 0.55 + 0.35 * Math.sin(time * 5);
    ctx.shadowColor = `hsl(${c.hue} 100% 75%)`;
    ctx.shadowBlur = 12;
    ctx.strokeStyle = `hsla(${c.hue} 100% 80% / ${crackleAlpha})`;
    ctx.lineWidth = Math.max(1.5, r * 0.05);
    ctx.beginPath();
    ctx.arc(cx, cy, outer + 1, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // ── Two slowly-orbiting bright spark dots ──
    for (let i = 0; i < 2; i++) {
      const a = rot * 1.8 + i * Math.PI;
      const px = cx + Math.cos(a) * outer;
      const py = cy + Math.sin(a) * outer;
      ctx.fillStyle = `hsl(${c.hue} 100% 90%)`;
      ctx.beginPath();
      ctx.arc(px, py, Math.max(1.2, r * 0.06), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 2× — gold disc + HUD ring with tick marks rotating in opposite directions
  // ─────────────────────────────────────────────────────────────────────────

  private drawDouble(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, r: number,
    c: { hue: number; sat: number; light: number },
    time: number,
  ): void {
    const coin = r * 0.62;
    const outerRing = r * 0.88;
    const innerRing = r * 0.74;

    // ── Outer HUD ring with tick marks, rotating CW ──
    const rotOuter = time * 0.55;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotOuter);
    ctx.shadowColor = `hsl(${c.hue} 100% 70%)`;
    ctx.shadowBlur = 10;
    ctx.strokeStyle = `hsla(${c.hue} 100% 70% / 0.7)`;
    ctx.lineWidth = Math.max(1.2, r * 0.04);
    ctx.beginPath();
    ctx.arc(0, 0, outerRing, 0, Math.PI * 2);
    ctx.stroke();
    // Tick marks every 30°, alternating short/long for a "compass" feel.
    const tickCount = 12;
    for (let i = 0; i < tickCount; i++) {
      const a = (i / tickCount) * Math.PI * 2;
      const isLong = i % 3 === 0;
      const t1 = outerRing - (isLong ? r * 0.14 : r * 0.07);
      const t2 = outerRing;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * t1, Math.sin(a) * t1);
      ctx.lineTo(Math.cos(a) * t2, Math.sin(a) * t2);
      ctx.lineWidth = isLong ? Math.max(1.5, r * 0.05) : Math.max(1, r * 0.03);
      ctx.strokeStyle = `hsla(${c.hue} 100% 80% / ${isLong ? 0.95 : 0.55})`;
      ctx.stroke();
    }
    ctx.restore();

    // ── Inner ring rotating CCW (counter-rotation reads as "tech HUD") ──
    const rotInner = -time * 0.85;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotInner);
    ctx.shadowBlur = 6;
    ctx.strokeStyle = `hsla(${c.hue} 100% 75% / 0.55)`;
    ctx.lineWidth = Math.max(1, r * 0.025);
    // Inner ring as a dashed arc — three gaps for a "loading bar" feel.
    const dashes = 3;
    for (let i = 0; i < dashes; i++) {
      const a0 = (i / dashes) * Math.PI * 2 + 0.12;
      const a1 = ((i + 1) / dashes) * Math.PI * 2 - 0.12;
      ctx.beginPath();
      ctx.arc(0, 0, innerRing, a0, a1);
      ctx.stroke();
    }
    ctx.restore();

    // ── Coin face ──
    ctx.shadowColor = `hsl(${c.hue} 100% 70%)`;
    ctx.shadowBlur = 12;
    const grad = ctx.createRadialGradient(cx - coin * 0.25, cy - coin * 0.3, 0, cx, cy, coin);
    grad.addColorStop(0,   `hsl(${c.hue} 100% 85%)`);
    grad.addColorStop(0.5, `hsl(${c.hue} 100% 60%)`);
    grad.addColorStop(1,   `hsl(${c.hue} 100% 38%)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, coin, 0, Math.PI * 2);
    ctx.fill();
    // Rim
    ctx.strokeStyle = `hsl(${c.hue} 100% 88%)`;
    ctx.lineWidth = Math.max(1.2, r * 0.04);
    ctx.beginPath();
    ctx.arc(cx, cy, coin, 0, Math.PI * 2);
    ctx.stroke();

    // ── "2×" glyph in dark teal so it reads against the gold ──
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#06080f';
    ctx.font = `bold ${Math.round(r * 0.70)}px "Space Grotesk", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('2×', cx, cy + r * 0.04);
  }
}
